"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function GetAFreeEstimate() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  return <section id="get-a-free-estimate" ref={ref} className="py-20 px-4 bg-[hsl(0,0%,97%)]">
      <div className={`max-w-2xl mx-auto transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}>
        <div className="text-center mb-10">
          <h2 className="text-4xl md:text-5xl font-display font-bold mb-4" style={{
          color: "var(--color-primary)"
        }}>
            Get A Free Estimate
          </h2>
          <p className="text-lg" style={{
          color: "var(--color-muted)"
        }}>
            Fill out the form below and we'll get back to you with a detailed quote for your plumbing needs.
          </p>
        </div>

        <FormProvider className="bg-[hsl(0,0%,97%)] rounded-lg p-8 shadow-[inset_0_2px_8px_rgba(0,0,0,0.08)]" formId="free_estimate_form_homepage2" sectionName="get-a-free-estimate" defaultValues={{
        name: "",
        email: "",
        phone: "",
        address: "",
        service_type: "",
        details: ""
      }} validationRules={{
        name: {
          required: true
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          required: true,
          pattern: "phone"
        },
        address: {
          required: true
        },
        service_type: {
          required: true
        }
      }} submitText="Request Free Estimate" successMessage="Thank you! We've received your request and will contact you shortly with your free estimate.">
          <EstimateForm />
        </FormProvider>
      </div>
    </section>;
}
function EstimateForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-6 rounded-lg border-2 border-[var(--color-secondary)] bg-[var(--color-secondary-light)] text-center">
        <p className="text-lg font-semibold mb-4" style={{
        color: "var(--color-secondary-dark)"
      }}>
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-2">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Your Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="bg-white shadow-[inset_0_1px_3px_rgba(0,0,0,0.06)] focus-visible:ring-[var(--color-primary)] focus-visible:border-[var(--color-primary)] transition-all duration-[var(--duration-fast)]" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="bg-white shadow-[inset_0_1px_3px_rgba(0,0,0,0.06)] focus-visible:ring-[var(--color-primary)] focus-visible:border-[var(--color-primary)] transition-all duration-[var(--duration-fast)]" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 123-4567" className="bg-white shadow-[inset_0_1px_3px_rgba(0,0,0,0.06)] focus-visible:ring-[var(--color-primary)] focus-visible:border-[var(--color-primary)] transition-all duration-[var(--duration-fast)]" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="address" rules={{
        required: "Address is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Project / Property Address</FormLabel>
              <FormControl>
                <Input placeholder="123 Main St, City, State, ZIP" className="bg-white shadow-[inset_0_1px_3px_rgba(0,0,0,0.06)] focus-visible:ring-[var(--color-primary)] focus-visible:border-[var(--color-primary)] transition-all duration-[var(--duration-fast)]" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="service_type" rules={{
      required: "Please select a service type"
    }} render={({
      field
    }) => <FormItem className="mt-6">
            <FormLabel>Type of Service Needed</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="w-full bg-white shadow-[inset_0_1px_3px_rgba(0,0,0,0.06)] focus-visible:ring-[var(--color-primary)] focus-visible:border-[var(--color-primary)] transition-all duration-[var(--duration-fast)]">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="Residential Plumbing">Residential Plumbing</SelectItem>
                <SelectItem value="Commercial Plumbing">Commercial Plumbing</SelectItem>
                <SelectItem value="Pump Repair / Replacement">Pump Repair / Replacement</SelectItem>
                <SelectItem value="Boiler / Heat Exchanger Service">Boiler / Heat Exchanger Service</SelectItem>
                <SelectItem value="Irrigation / Pool System">Irrigation / Pool System</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="details" render={({
      field
    }) => <FormItem className="mt-6">
            <FormLabel>Additional Details</FormLabel>
            <FormControl>
              <textarea placeholder="Describe the issue or project requirements" className="w-full min-h-[120px] px-3 py-2 rounded-md border bg-white shadow-[inset_0_1px_3px_rgba(0,0,0,0.06)] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--color-primary)]/50 focus-visible:border-[var(--color-primary)] transition-all duration-[var(--duration-fast)] text-sm" {...field} />
            </FormControl>
            <p className="text-sm mt-1" style={{
        color: "var(--color-muted)"
      }}>
              The more information you provide, the more accurate your estimate will be.
            </p>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="mt-6 p-4 rounded-lg border-2 border-red-200 bg-red-50">
          <p className="text-red-800">{submitError}</p>
        </div>}

      <div className="mt-8">
        <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-semibold rounded-md shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)]" style={{
        backgroundColor: "var(--color-accent)",
        color: "var(--color-foreground-accent)"
      }}>
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Sending Request...
            </> : "Request Free Estimate"}
        </Button>
      </div>
    </Form>;
}